import { http } from '@inertiajs/core';

/** The identity belongs to this document, never to a cookie shared by other tabs. */
export function registerIdentityGeneration(generation: string): void {
    const header = 'X-Identity-Generation';
    const local = (url: string | URL): boolean =>
        new URL(String(url), window.location.href).origin ===
        window.location.origin;
    const client = http.getClient();
    http.setClient({
        async request(config) {
            const response = await client.request({
                ...config,
                headers: local(config.url)
                    ? { ...config.headers, [header]: generation }
                    : config.headers,
            });
            if (response.status === 409) {
                try {
                    if (JSON.parse(response.data)?.reload === true)
                        window.location.reload();
                } catch {
                    /* Non-JSON responses are handled by their caller. */
                }
            }
            return response;
        },
    });

    const fetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
        const url = input instanceof Request ? input.url : input;
        if (!local(url)) return fetch(input, init);
        const headers = new Headers(
            init?.headers ??
                (input instanceof Request ? input.headers : undefined),
        );
        headers.set(header, generation);
        const response = await fetch(input, { ...init, headers });
        if (response.status === 409) {
            const data = await response
                .clone()
                .json()
                .catch(() => null);
            if (data?.reload === true) window.location.reload();
        }
        return response;
    };

    // Axios and other XHR callers share the same immutable document identity.
    const requests = new WeakMap<XMLHttpRequest, boolean>();
    const tagged = new WeakSet<XMLHttpRequest>();
    const setRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
        if (name.toLowerCase() === header.toLowerCase()) tagged.add(this);
        setRequestHeader.call(this, name, value);
    };
    const open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (
        method,
        url,
        async = true,
        username = null,
        password = null,
    ) {
        requests.set(this, local(url));
        tagged.delete(this);
        open.call(this, method, url, async, username, password);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
        if (requests.get(this)) {
            if (!tagged.has(this)) this.setRequestHeader(header, generation);
            this.addEventListener('load', () => {
                if (this.status !== 409) return;
                try {
                    if (JSON.parse(this.responseText)?.reload === true)
                        window.location.reload();
                } catch {
                    /* Non-JSON responses are handled by their caller. */
                }
            });
        }
        send.call(this, body);
    };
    const tagForm = (form: HTMLFormElement): void => {
        if (!local(form.action)) return;
        let input = form.querySelector<HTMLInputElement>(
            'input[name="_identity_generation"]',
        );
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = '_identity_generation';
            form.append(input);
        }
        input.value = generation;
    };
    document.addEventListener(
        'submit',
        (event) => {
            if (event.target instanceof HTMLFormElement) tagForm(event.target);
        },
        true,
    );
    const submit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function () {
        tagForm(this);
        submit.call(this);
    };
}
